import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const SecuritySection = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.length >= 12) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 12.5;
    if (/[^a-zA-Z0-9]/?.test(password)) strength += 12.5;
    return strength;
  };

  const handlePasswordChange = (value) => {
    setFormData({ ...formData, newPassword: value });
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const getStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-info';
    return 'bg-success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!formData?.currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!formData?.newPassword) {
      setError('New password is required');
      return;
    }

    if (formData?.newPassword?.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData?.newPassword !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData?.currentPassword === formData?.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: formData?.newPassword
      });

      if (updateError) {
        setError(updateError?.message || 'Failed to update password');
      } else {
        setSuccess('Password updated successfully');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordStrength(0);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-20 md:p-24">
      <div className="flex items-center gap-12 mb-20">
        <Icon name="Lock" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-semibold text-card-foreground">Security Settings</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-24">
        Update your password and manage security preferences
      </p>

      {success && (
        <div className="mb-20 p-16 bg-success/10 border border-success/20 rounded-lg flex items-start gap-12">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-sm text-success flex-1">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-20 p-16 bg-error/10 border border-error/20 rounded-lg flex items-start gap-12">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          <p className="text-sm text-error flex-1">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-20">
        <Input
          label="Current Password"
          type="password"
          placeholder="Enter current password"
          value={formData?.currentPassword}
          onChange={(e) => setFormData({ ...formData, currentPassword: e?.target?.value })}
          required
        />

        <div>
          <Input
            label="New Password"
            type="password"
            placeholder="Enter new password"
            value={formData?.newPassword}
            onChange={(e) => handlePasswordChange(e?.target?.value)}
            required
          />
          {formData?.newPassword && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-8">
                <span className="text-xs text-muted-foreground">Password Strength:</span>
                <span className="text-xs font-medium text-foreground">{getStrengthLabel()}</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${getStrengthColor()}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <Input
          label="Confirm New Password"
          type="password"
          placeholder="Confirm new password"
          value={formData?.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e?.target?.value })}
          required
        />

        <div className="flex justify-end pt-16">
          <Button
            type="submit"
            variant="default"
            loading={loading}
            iconName="Lock"
            iconPosition="left"
          >
            Update Password
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SecuritySection;